import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Download } from 'lucide-react';
import { useState } from 'react';
import {
  AmountHeads,
  DocLink,
  HeadCells,
  MonthPicker,
  Section,
} from '@/components/gst/return-parts';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import {
  downloadReturnJson,
  lastMonth,
  monthLabel,
  periodRange,
  stateLabel,
  useGstr1,
  type GstInvoice,
  type Gstr1View,
} from '@/lib/gst';
import { i18n, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/taxes/gst/gstr1')({
  validateSearch: (s: Record<string, unknown>): { period?: string } =>
    typeof s['period'] === 'string' && /^\d{4}-\d{2}$/.test(s['period'])
      ? { period: s['period'] }
      : {},
  component: Gstr1Page,
});

const NIL_TYPES = ['INTRB2B', 'INTRAB2B', 'INTRB2C', 'INTRAB2C'] as const;
/** Table 8 supply type in the reader's language (unknown types show as sent). */
const nilLabel = (k: string): string =>
  (NIL_TYPES as readonly string[]).includes(k)
    ? i18n.t(`taxPages.gstr1.nil.${k as (typeof NIL_TYPES)[number]}`)
    : k;

/** The summary rows' labels as the API sends them (tax pack gstr1.ts), keyed for the catalogue. */
const SUMMARY_ROWS = {
  'B2B invoices': 'b2b',
  'B2C (large) invoices': 'b2cl',
  Exports: 'exp',
  'B2C (others)': 'b2cs',
  'Credit/debit notes (registered)': 'cdnr',
  'Credit/debit notes (unregistered)': 'cdnur',
  'Nil rated, exempted and non-GST supplies': 'nil',
} as const;
const summaryLabel = (label: string): string =>
  label in SUMMARY_ROWS
    ? i18n.t(`taxPages.gstr1.summaryRow.${SUMMARY_ROWS[label as keyof typeof SUMMARY_ROWS]}`)
    : label;

/** Table 13 document types (GSTN doc_num) in the reader's language. */
const DOC_TYPES = [1, 5, 9, 10, 11, 12] as const;
const docTypeLabel = (d: { docType: number; label: string }): string =>
  (DOC_TYPES as readonly number[]).includes(d.docType)
    ? i18n.t(`taxPages.gstr1.docType.d${d.docType as (typeof DOC_TYPES)[number]}`)
    : d.label;

/** Screen T7 (document 06): GSTR-1 for a month. */
function Gstr1Page() {
  const t = useT();
  const orgId = useOrgId();
  const navigate = useNavigate();
  const period = Route.useSearch().period ?? lastMonth();
  const r = useGstr1(period);
  const [busy, setBusy] = useState(false);
  const { from, to } = periodRange(period);

  return (
    <>
      <PageHeader
        title="GSTR-1"
        description={t('taxPages.gstr1.description')}
        actions={
          <>
            <MonthPicker
              period={period}
              onChange={(p) => void navigate({ to: '/taxes/gst/gstr1', search: { period: p } })}
            />
            <Button asChild variant="outline">
              <Link
                to="/reports/$reportKey"
                params={{ reportKey: 'gstr-1' }}
                search={{ from, to } as never}
              >
                {t('taxPages.gst.openAsReport')}
              </Link>
            </Button>
            <Button
              disabled={!r.data?.gstin || busy}
              title={r.data && !r.data.gstin ? t('taxPages.gst.addGstin') : undefined}
              onClick={async () => {
                setBusy(true);
                try {
                  await downloadReturnJson(orgId!, 'gstr1', period, r.data!.gstin!);
                } catch (e) {
                  toast((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Download className="size-4" /> {t('taxPages.gst.downloadJson')}
            </Button>
          </>
        }
      />
      <p className="mb-4 text-sm">
        <Link to="/taxes/gst" className="text-primary hover:underline">
          {t('taxPages.gst.title')}
        </Link>{' '}
        › {monthLabel(period)}
        {r.data ? ` ${t('taxPages.gst.dueOn', { date: displayDate(r.data.dueDate) })}` : ''}
      </p>
      {r.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : r.isError ? (
        <EmptyState title={t('taxPages.gstr1.loadError')} hint={r.error.message} />
      ) : (
        <Gstr1Body r={r.data} />
      )}
    </>
  );
}

function Gstr1Body({ r }: { r: Gstr1View }) {
  const t = useT();
  const kind = (id: string | null) => (id ? (r.entries[id]?.kind ?? 'invoice') : 'invoice');
  const invoices = (title: string, list: GstInvoice[], testId: string) =>
    list.length ? (
      <Section title={title} testId={testId}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('taxPages.eInvoicing.col.number')}</TableHead>
              <TableHead className="w-[110px]">{t('taxPages.eInvoicing.col.date')}</TableHead>
              <TableHead>{t('taxPages.eInvoicing.col.customer')}</TableHead>
              <TableHead className="w-[170px]">GSTIN</TableHead>
              <TableHead className="w-[170px]">{t('taxPages.gst.placeOfSupply')}</TableHead>
              <TableHead className="w-[120px] text-end">{t('taxPages.gst.value')}</TableHead>
              <AmountHeads />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((i) => (
              <TableRow key={`${i.documentId}-${i.number}`}>
                <TableCell>
                  {i.documentId ? (
                    <DocLink id={i.documentId} kind={kind(i.documentId)}>
                      {i.number}
                    </DocLink>
                  ) : (
                    i.number
                  )}
                </TableCell>
                <TableCell>{displayDate(i.date)}</TableCell>
                <TableCell>{i.contactName}</TableCell>
                <TableCell className="tnum">{i.gstin ?? ''}</TableCell>
                <TableCell>{stateLabel(i.placeOfSupply)}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(i.value)}</TableCell>
                <HeadCells a={i.totals} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    ) : null;

  return (
    <div className="space-y-6">
      {r.findings.length ? (
        <div
          className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-sm"
          role="note"
        >
          <p className="mb-1 inline-flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-4 text-[#B45309]" aria-hidden />{' '}
            {t('taxPages.gstr1.exceptionsToReview', { count: r.findings.length })}
          </p>
          <ul className="list-disc ps-5">
            {r.findings.map((f, n) => (
              <li key={n}>{f.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <Section title={t('taxPages.gstr1.summary')} testId="gstr1-summary">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">{t('taxPages.gstr1.colTable')}</TableHead>
              <TableHead>{t('taxPages.gstr1.colSupplies')}</TableHead>
              <TableHead className="w-[80px] text-end">{t('taxPages.gstr1.colCount')}</TableHead>
              <TableHead className="w-[130px] text-end">{t('taxPages.gst.value')}</TableHead>
              <AmountHeads />
            </TableRow>
          </TableHeader>
          <TableBody>
            {r.summary.map((s) => (
              <TableRow key={`${s.table}-${s.label}`}>
                <TableCell>{s.table}</TableCell>
                <TableCell>{summaryLabel(s.label)}</TableCell>
                <TableCell className="tnum text-end">{s.count}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(s.value)}</TableCell>
                <HeadCells a={s} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      {invoices(t('taxPages.gstr1.section.b2b'), r.b2b, 'gstr1-b2b')}
      {invoices(t('taxPages.gstr1.section.b2cl'), r.b2cl, 'gstr1-b2cl')}
      {invoices(t('taxPages.gstr1.section.exp'), r.exp, 'gstr1-exp')}
      {r.b2cs.length ? (
        <Section title={t('taxPages.gstr1.section.b2cs')} testId="gstr1-b2cs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('taxPages.gst.placeOfSupply')}</TableHead>
                <TableHead className="w-[120px]">{t('taxPages.gstr1.colSupply')}</TableHead>
                <TableHead className="w-[80px] text-end">{t('taxPages.gstr1.colRate')}</TableHead>
                <AmountHeads />
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.b2cs.map((x) => (
                <TableRow key={`${x.supplyType}-${x.placeOfSupply}-${x.rate}`}>
                  <TableCell>{stateLabel(x.placeOfSupply)}</TableCell>
                  <TableCell>
                    {x.supplyType === 'INTER'
                      ? t('taxPages.gst.interState')
                      : t('taxPages.gst.intraState')}
                  </TableCell>
                  <TableCell className="tnum text-end">{rate(x.rate)}</TableCell>
                  <HeadCells a={x} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ) : null}
      {r.nil.length ? (
        <Section title={t('taxPages.gstr1.section.nil')} testId="gstr1-nil">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('taxPages.gstr1.colSupplies')}</TableHead>
                <TableHead className="w-[130px] text-end">
                  {t('taxPages.gstr1.colNilRated')}
                </TableHead>
                <TableHead className="w-[130px] text-end">
                  {t('taxPages.gstr1.colExempted')}
                </TableHead>
                <TableHead className="w-[130px] text-end">
                  {t('taxPages.gstr1.colNonGst')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.nil.map((x) => (
                <TableRow key={x.supplyType}>
                  <TableCell>{nilLabel(x.supplyType)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(x.nil)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(x.exempt)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(x.nonGst)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ) : null}
      {invoices(t('taxPages.gstr1.section.cdnr'), r.cdnr, 'gstr1-cdnr')}
      {invoices(t('taxPages.gstr1.section.cdnur'), r.cdnur, 'gstr1-cdnur')}

      {(['b2b', 'b2c'] as const).map((k) =>
        r.hsn[k].length ? (
          <Section
            key={k}
            title={t('taxPages.gstr1.section.hsn', { kind: k === 'b2b' ? 'B2B' : 'B2C' })}
            testId={`gstr1-hsn-${k}`}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">HSN/SAC</TableHead>
                  <TableHead>{t('taxPages.vatReturn.colDescription')}</TableHead>
                  <TableHead className="w-[60px]">{t('taxPages.gstr1.colUqc')}</TableHead>
                  <TableHead className="w-[100px] text-end">
                    {t('taxPages.gstr1.colQuantity')}
                  </TableHead>
                  <TableHead className="w-[70px] text-end">{t('taxPages.gstr1.colRate')}</TableHead>
                  <AmountHeads />
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.hsn[k].map((h) => (
                  <TableRow key={`${h.hsn}-${h.rate}-${h.uqc}`}>
                    <TableCell className="tnum">{h.hsn || '—'}</TableCell>
                    <TableCell>{h.description}</TableCell>
                    <TableCell>{h.uqc}</TableCell>
                    <TableCell className="tnum text-end">{h.quantity}</TableCell>
                    <TableCell className="tnum text-end">{rate(h.rate)}</TableCell>
                    <HeadCells a={h} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        ) : null,
      )}

      {r.docs.length ? (
        <Section title={t('taxPages.gstr1.section.docs')} testId="gstr1-docs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('taxPages.gstr1.colNature')}</TableHead>
                <TableHead className="w-[120px]">{t('taxPages.gstr1.colFrom')}</TableHead>
                <TableHead className="w-[120px]">{t('taxPages.gstr1.colTo')}</TableHead>
                <TableHead className="w-[90px] text-end">{t('taxPages.gstr1.colTotal')}</TableHead>
                <TableHead className="w-[90px] text-end">
                  {t('taxPages.gstr1.colCancelled')}
                </TableHead>
                <TableHead className="w-[90px] text-end">
                  {t('taxPages.gstr1.colNetIssued')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.docs.map((d) => (
                <TableRow key={d.docType}>
                  <TableCell>{docTypeLabel(d)}</TableCell>
                  <TableCell>{d.from}</TableCell>
                  <TableCell>{d.to}</TableCell>
                  <TableCell className="tnum text-end">{d.total}</TableCell>
                  <TableCell className="tnum text-end">{d.cancelled}</TableCell>
                  <TableCell className="tnum text-end">{d.net}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ) : null}
    </div>
  );
}

const rate = (r: string) => `${r.replace(/\.?0+$/, '')}%`;
